import React, { useState, useEffect, useLayoutEffect } from "react";
import { getOverviewChannels } from "./channelCache";
import { IOverviewChannel, IChannelTS, IChannelFC, getChannelTS } from "../../../services/api/Dashboard";
import Year, { IYear } from "../../../services/api/Year";
import Month, { IMonth } from "../../../services/api/Month";
import ChannelType, { IChannelType } from "../../../services/api/ChannelType";
import Channel, { IChannel } from "../../../services/api/Channel";
import Loading from "../../../components/Loading";
import Select from "../../../components/Form/Select";
import Stack from "./charts/Stack";

import calendarIcon from "../assets/images/calendar.svg";
import eventIcon from "../assets/images/event.svg";
import Formatter from "../../../services/utils/Formatter";

interface IActualInfo {
	ACT: number;
}

interface IGraphData {
	TotalBase: number;
	TotalBaseLY: number;
	TotalBaseYTD: number;
	TotalBaseLYYTD: number;
	TotalW: IActualInfo;
	TotalWYTD: IActualInfo;
	TotalMTW: IActualInfo;
	TotalMTWYTD: IActualInfo;
}

export interface ICountryCloseupTableContent {
	name: string;
	valueBaseLY: number;
	valueBase: number;
	tsValue: number;
	valueBaseLYYTD: number;
	valueBaseYTD: number;
	tsValueYTD: number;
}

interface ICountryCloseupProps {

}

type ActualKeysTS = "ACT";

type ActualKeysFC = "ACT";

function handleDefaultReference(channelType: string) {
	return "BP";
	//return channelType === "source" ? "FRE" : "BP"
}

const CountryCloseup: React.FC<ICountryCloseupProps> = () => {
	const [loading, isLoading] = useState<Boolean>(true);
	const [years, setYears] = useState<IYear[]>([]);
	const [months, setMonths] = useState<IMonth[]>([]);
	const [channelTypes, setChannelTypes] = useState<IChannelType[]>([]);
	const [channels, setChannels] = useState<IChannel[]>([]);
	const [countries, setCountries] = useState<IOverviewChannel[]>([]);
	const [countriesYTD, setCountriesYTD] = useState<IOverviewChannel[]>([]);
	const [groups, setGroups] = useState<IOverviewChannel[]>([]);
	const [groupsYTD, setGroupsYTD] = useState<IOverviewChannel[]>([]);
	const [current, setOverview] = useState<IOverviewChannel>({} as IOverviewChannel);
	const [, setOverviewYTD] = useState<IOverviewChannel>({} as IOverviewChannel);
	const [active, setActive] = useState<IOverviewChannel>({} as IOverviewChannel);
	const [activeYTD, setActiveYTD] = useState<IOverviewChannel>({} as IOverviewChannel);

	const [loadTable, setLoadTable] = useState<Boolean>(true);
	const [tableContent, setTableContent] = useState<Array<ICountryCloseupTableContent>>([]);
	const [, setGraphContent] = useState<IGraphData>({} as IGraphData);
	const [monthData, setMonthData] = useState<any[]>([]);
	const [monthDataYTD, setMonthDataYTD] = useState<any[]>([]);

	const [year, setYear] = useState<number>(0);
	const [month, setMonth] = useState<number>(0);
	const [channelType, setChannelType] = useState<string>("segment");
	const [tstype, setType] = useState<string>(handleDefaultReference(channelType));
	const [fWeek] = useState<string>("ACT");

	const [selectedGroup, setSelectedGroup] = useState<string>();
	const [selectedCountry, setSelectedCountry] = useState<string>();

	const date = new Date();
	// Se o mês for janeiro (0), ajusta para dezembro do ano anterior
	if (date.getMonth() === 0) {
		date.setFullYear(date.getFullYear() - 1);  // Ajusta o ano para o anterior
		date.setMonth(11);  // Define o mês como dezembro (11)
	} else {
		date.setMonth(date.getMonth() - 1);  // Subtrai 1 do mês, caso não seja janeiro
	}

	let totals: ICountryCloseupTableContent = {
		name: "TOTAL",
		valueBaseLY: 0,
		valueBase: 0,
		tsValue: 0,
		valueBaseLYYTD: 0,
		valueBaseYTD: 0,
		tsValueYTD: 0,
	};

	function populateCountry(obj: IOverviewChannel): IOverviewChannel[] {
		let countriesTmp: IOverviewChannel[] = [];

		if (obj.longitude !== 0 && obj.latitude !== 0)
			countriesTmp.push(obj);

		if (obj.children) {
			obj.children.forEach((objC) => {
				countriesTmp = countriesTmp.concat(populateCountry(objC));
			});
		}

		return countriesTmp;
	}

	function populateGroup(obj: IOverviewChannel): IOverviewChannel[] {
		let groupsTmp: IOverviewChannel[] = [];

		if (obj.latitude === 0 || obj.longitude === 0) 
			groupsTmp.push(obj);
		

		if (obj.children) {
			obj.children.forEach((objC) => {
				groupsTmp = groupsTmp.concat(populateGroup(objC));
			});
		}

		return groupsTmp;
	}

	async function LoadOverview(year: number, month: number, channelType: string) {
		isLoading(true);

		const [{ overview, ytdOverview }, channelsResult] = await Promise.all([
			getOverviewChannels(year, month, channelType),
			Channel.GetAll(channelType),
		]);
		setOverview(overview)
		setOverviewYTD(ytdOverview);

		let countriesTmp: IOverviewChannel[] = populateCountry(overview);
		let countriesTmpYTD: IOverviewChannel[] = populateCountry(ytdOverview);

		setCountries(countriesTmp);
		setCountriesYTD(countriesTmpYTD);

		let groupsTmp: IOverviewChannel[] = populateGroup(overview);
		let groupsTmpYTD: IOverviewChannel[] = populateGroup(ytdOverview);
		setGroups(groupsTmp);
		setGroupsYTD(groupsTmpYTD);


		const colors = [
			"#f91054", "#fcc003", "#00162b", "#656061", 
			"#B26B78", "#F4801F", "#2D5072",
			"#d94063", "#ffbc03", "#00335e", "#9d8e85",
			"#c9a1b4", "#fb7c1b", "#4f7690",
			"#f9c6d3", "#f2e3c9", "#8e9a99", "#f9a3c1",
			"#ff934b", "#5a728b"
		];

		channelsResult.forEach((channel, index) => {
			channel.color = colors[index];
		})

		setChannels(channelsResult)

		//show data
		if (overview.code !== "LATAM") {
			// Pegar o primeiro país do array countriesTmp
			const firstCountry = countriesTmp[0];
			setActive(firstCountry);
			
			// Encontrar o país correspondente em countriesTmpYTD pelo ID
			const firstCountryYTD = countriesTmpYTD.find(country => country.id === firstCountry.id);
			
			if (firstCountryYTD) {
				setActiveYTD(firstCountryYTD);
			} else {
				// Fallback caso não encontre o país pelo ID
				setActiveYTD(countriesTmpYTD[0]);
			}
			
			setSelectedGroup("");
			setSelectedCountry(firstCountry.id.toString());
		}
		else if (selectedGroup != null && selectedGroup != "") {
			let idxGroup = new Number(selectedGroup) as number;
			setActive(groupsTmp[idxGroup]);
			setActiveYTD(groupsTmpYTD[idxGroup]);
			setSelectedGroup(idxGroup + "");
			setSelectedCountry("");
		}
		else if (selectedCountry != null && selectedCountry != "") {
			const countryId = selectedCountry;
			
			// Encontrar o país no array countriesTmp pelo ID
			const selectedCountryObj = countriesTmp.find(country => country.id.toString() === countryId);
			
			if (selectedCountryObj) {
				setActive(selectedCountryObj);
				
				// Encontrar o país correspondente em countriesTmpYTD pelo mesmo ID
				const selectedCountryYTD = countriesTmpYTD.find(country => country.id.toString() === countryId);
				
				if (selectedCountryYTD) {
					setActiveYTD(selectedCountryYTD);
				} else {
					// Opcional: fallback para o primeiro país ou outra lógica
				}
				
				setSelectedGroup("");
				setSelectedCountry(countryId);
			} else {
				// Opcional: fallback para o primeiro país ou outra lógica
			}
		}
		else {
			//show LATAM data
			setActive(groupsTmp[0]);
			setActiveYTD(groupsTmpYTD[0]);
			setSelectedGroup("0");
			setSelectedCountry("");
		}

		isLoading(false);
	}

	useEffect(() => {
	}, [selectedCountry])

	useLayoutEffect(() => {
		Promise.all([Year.GetAll(), Month.GetAll(), ChannelType.GetAll()])
			.then(async (value) => {
				const years = value[0].filter((value) => { return value.active });
				setYears(years);
				setMonths(value[1]);
				setChannelTypes(value[2]);

				setYear(date.getFullYear());
				setMonth(date.getMonth() + 1);
				setChannelType(channelTypes[0]?.slug ?? "segment")

				LoadOverview(years[0].name, date.getMonth() + 1, channelType);
			})
			.catch(() => undefined);
		
	}, []);

	useEffect(() => {
		const loadOverviewAsync = async () => {
			if (!loading)
				await LoadOverview(year, month, channelType);
		}

		loadOverviewAsync();
	}, [year, month, channelType]);
	
	function mapList(obj: any): any {
		return { value: obj.id, label: obj.name };
	}

	function mapChannelTypeList(obj: any): any {
		return { value: obj.slug, label: obj.name };
	}

	const selectReferenceOptions = 
		[
			{ value: "BP", label: "BP" },
			{ value: "FRE", label: "FRE" },
			{ value: "SRE", label: "SRE" },
		]

	let label: any = {
		"ACT": "ACT",
	}
	useEffect(() => {
		if (active
			&& activeYTD
			&& Object.keys(active).length > 0
			&& Object.keys(activeYTD).length > 0) {
			getActiveCountryData(active, activeYTD);
		}


	}, [active, activeYTD, fWeek, tstype, channelType]);



	useEffect(() => {
		setType(handleDefaultReference(channelType));
	}, [channelType]);

	function getActiveCountryData(country: IOverviewChannel, countryYTD: IOverviewChannel) {
		setLoadTable(true);

		const actualInfo: IActualInfo = {
			ACT: 0,
		};

		const countryGraphData: IGraphData = {
			TotalBase: 0,
			TotalBaseLY: 0,
			TotalBaseLYYTD: 0,
			TotalBaseYTD: 0,
			TotalW: { ...actualInfo },
			TotalWYTD: { ...actualInfo },
			TotalMTW: { ...actualInfo },
			TotalMTWYTD: { ...actualInfo },
		}

		const countryData: Array<ICountryCloseupTableContent> = [];


		const month: any = [
			{ category: "LY" },
			{ category: tstype },
			{ category: label[fWeek] }
		];
		const monthYTD: any = [
			{ category: "LY" },
			{ category: tstype },
			{ category: label[fWeek] }
		];

		(country?.actualChannels || []).forEach((obj: IChannelFC, i: number) => {
			const channelTS: IChannelTS = country.planChannels[i];
			const channelFC: IChannelFC = country.actualChannels[i];
			const channelFCYTD: IChannelFC = countryYTD.actualChannels[i];
			const channelTSYTD: IChannelTS = countryYTD.planChannels[i];

			const valueBase: number = getChannelTS(channelTS, tstype);
			let valueBaseYTD: number = getChannelTS(channelTSYTD, tstype);
			let valueBaseLY: number = 0;
			let valueBaseLYYTD: number = 0;

			//valueBaseYTD += valueBase;

			// #region TS Mapping

			const tsValueMapping: { [key in ActualKeysTS]: number } = {
				ACT: obj?.actual ?? 0,
			};

			const tsValue: number = tsValueMapping[fWeek as ActualKeysTS] || 0;

			// #endregion 

			// #region TS YTD Mapping

			const tsValueYTDMapping: { [key in ActualKeysTS]: number } = {
				ACT: channelFCYTD?.actual ?? 0,
			};
			let tsValueYTD: number = tsValueYTDMapping.ACT || 0;
			//get current value + YTD
			//tsValueYTD += tsValue;
			// #endregion

			// #region TS LY Mapping
			const tsValueLYMapping: { [key in ActualKeysTS]: number } = {
				ACT: country.lastYear.actualChannels?.[i]?.actual ?? 0,
			};

			valueBaseLY = tsValueLYMapping[fWeek as ActualKeysFC] || 0;

			// #endregion

			// #region TS LYYTD Mapping
			const actualLYYTDMapping: { [key in ActualKeysTS]: number } = {
				ACT: countryYTD.lastYear.actualChannels?.[i]?.actual ?? 0
			};

			valueBaseLYYTD = actualLYYTDMapping[fWeek as ActualKeysFC] || 0;

			// #endregion
			countryData.push({
				name: obj.name,
				tsValue,
				tsValueYTD,
				valueBase,
				valueBaseLY,
				valueBaseLYYTD,
				valueBaseYTD
			});

			//#region GRAPH DATA
			function updateBaseData(target: IGraphData, base: number, baseLY: number, baseYTD: number, baseLYYTD: number) {
				target.TotalBase += base;
				target.TotalBaseLY += baseLY;
				target.TotalBaseYTD += baseYTD;
				target.TotalBaseLYYTD += baseLYYTD;
			}

			// Função para atualizar valores semanais
			function updateWeeklyData(target: IActualInfo, obj?: IChannelFC, channelTS?: IChannelTS) {
				target.ACT += obj?.actual ?? 0;
			}

			// Função para atualizar valores acumulados YTD
			function updateYTDData(target: IActualInfo, channelFC?: IChannelFC, channelTSYTD?: IChannelTS, channelTS?: IChannelTS) {
				target.ACT += (channelFC?.actual ?? 0);
			}


			// Atualização dos dados do gráfico
			updateBaseData(countryGraphData, valueBase, valueBaseLY, valueBaseYTD, valueBaseLYYTD);
			updateWeeklyData(countryGraphData.TotalW, obj, channelTS);
			updateYTDData(countryGraphData.TotalWYTD, channelFC, channelTSYTD, channelTS);
			//#endregion

			//LY			
			month[0][obj.name] = valueBaseLY;
			monthYTD[0][obj.name] = valueBaseLYYTD;

			//CRE
			month[1][obj.name] = valueBase;
			monthYTD[1][obj.name] = valueBaseYTD;

			//FCT
			month[2][obj.name] = tsValue;
			monthYTD[2][obj.name] = tsValueYTD;
		})

		setTableContent(countryData);
		setGraphContent(countryGraphData);
		setMonthData(month);
		setMonthDataYTD(monthYTD);

		setLoadTable(false);

	};

	function getFilterTitle(): string {
		if(active)
		 return `- ${active.code}`;

		return ''
	}

	function mapTable(country: ICountryCloseupTableContent) {
		return (
			<tr key={"channels" + country.name}>
				<td>{country.name}</td>

				{/* MONTH */}
				<td>{Formatter.DisplayValue(country.tsValue)}</td>
				<td>{Formatter.DisplayPercent((country.tsValue / country.valueBaseLY - 1) * 100)}</td>
				<td>{Formatter.DisplayPercent((country.tsValue / country.valueBase - 1) * 100)}</td>

    			{/* YTD */}
				<td>{Formatter.DisplayValue(country.tsValueYTD)}</td>
				<td>{Formatter.DisplayPercent((country.tsValueYTD / country.valueBaseLYYTD - 1) * 100)}</td>
				<td>{Formatter.DisplayPercent((country.tsValueYTD / country.valueBaseYTD  - 1) * 100)}</td>
			</tr>
		);
	}

	function mapTableTotals(tableContent: ICountryCloseupTableContent[]) {
		if (tableContent && tableContent.length > 0) {
			tableContent.forEach(country => {
				totals.valueBaseLY += country.valueBaseLY ?? 0; // Month Plan LY
				totals.valueBase += country.valueBase ?? 0; // Month Plan
				totals.tsValue += country.tsValue ?? 0; // Month Actual
				
				totals.valueBaseLYYTD += country.valueBaseLYYTD ?? 0; // YTD Plan LY
				totals.valueBaseYTD += country.valueBaseYTD ?? 0; // YTD Plan
				totals.tsValueYTD += country.tsValueYTD ?? 0; // YTD Actual
			});
		}

		return mapTable(totals);
	}


	function renderCountryCloseup() {
		return (
			<React.Fragment>
			<section className="content__block">

				<div className="content__header">
					<h2 className="content__title">Plan {getFilterTitle()}</h2>
					<div className="multi-selects">
						<div className="content__select">
							<Select
								className="select-custom select-custom--medium select-custom--shadow"
								name="fGroup"
								title="Group"
								options={groups.map((obj: IOverviewChannel, i: number) => {
									return { value: i, label: obj.name };
								})}
								value={selectedGroup}
								handleChange={(event: React.FormEvent<HTMLSelectElement>) => {
									let i = +event.currentTarget.value;
									if (i >= 0) {
										setActive(groups[i]);
										setActiveYTD(groupsYTD[i]);
									}

									setSelectedGroup(i + "");
									setSelectedCountry("");
								}}
							/>
						</div>
						<div className="content__select">
						<Select
							className="select-custom select-custom--medium select-custom--shadow"
							name="fCountry"
							title="Country"
							options={countries.map((obj: IOverviewChannel) => {
								return { value: obj.id, label: obj.name };
							})}
							value={selectedCountry}
							handleChange={(event: React.FormEvent<HTMLSelectElement>) => {
								const countryId = event.currentTarget.value;
								if (countryId) {
									// Encontrar o país no array countries pelo ID
									const countryIndex = countries.findIndex(country => country.id.toString() === countryId);
									
									if (countryIndex >= 0) {
										setActive(countries[countryIndex]);
										
										// Encontrar o país correspondente no array countriesYTD pelo ID
										const countryYTDIndex = countriesYTD.findIndex(country => country.id.toString() === countryId);
										
										if (countryYTDIndex >= 0) {
											setActiveYTD(countriesYTD[countryYTDIndex]);
										}
									}
									
									setSelectedCountry(countryId);
									setSelectedGroup("");
								}
							}}
						/>
						</div>
					</div>
				</div>
				<div className="content__header">
					<h2 className="content__title"></h2>
					<div className="multi-selects">
						<div className="content__select">
							<Select
								className="select-custom select-custom--medium select-custom--shadow"
								name="channelType"
								title="Type"
								options={channelTypes.map(mapChannelTypeList)}
								value={channelType}
								handleChange={(event: React.FormEvent<HTMLSelectElement>) => {
									var value = event.currentTarget.value;
									if (value)
										setChannelType(value);
								}}
							/>

							<Select
								className="select-custom select-custom--medium select-custom--shadow"
								name="month"
								title="Month"
								options={months.map(mapList)}
								value={month}
								handleChange={(event: React.FormEvent<HTMLSelectElement>) => {
									var value = +event.currentTarget.value;
									if (value)
										setMonth(value);
								}}
							/>
						</div>
						<div className="content__select">
							<Select
								className="select-custom select-custom--medium select-custom--shadow"
								name="year"
								title="Year"
								options={years.map((obj) => { return { value: obj.name, label: obj.name } })}
								value={year}
								handleChange={(event: React.FormEvent<HTMLSelectElement>) => {
									var value = +event.currentTarget.value;
									if (value)
										setYear(value);
								}}
							/>
						</div>
					</div>
				</div>
				<div className="content__select">
					<Select
						name="tstype1"
						title="Reference"
						className="select-custom select-custom--small"
						options={selectReferenceOptions}
						value={tstype}
						handleChange={(event: React.FormEvent<HTMLSelectElement>) => {
							var value = event.currentTarget.value;
							setType(value);
						}}
					/>
				</div>
				<div className="table">
					<table className="table__data table--row-align-text-right">
						<tbody>
							<tr className="table__data--borderless">
								<td colSpan={4}>
									<img src={calendarIcon} alt="Calendar" className="table__title-img" />
									<span className="table__title">Month</span>
								</td>
								<td colSpan={4}>
									<img src={eventIcon} alt="Event" className="table__title-img" />
									<span className="table__title">YTD</span>
								</td>
							</tr>
							<tr className="table__data--borderless">
								<td></td>
								<td colSpan={3}>
									<span className="table_subtitle">Actual {current.year}</span>
								</td>
								<td colSpan={3}>
									<span className="table_subtitle">Actual {current.year}</span>
								</td>
							</tr>
							<tr>
								<th></th>
								<th>{label[fWeek]}</th>
								<th>VS. LY</th>
								<th>VS. {tstype}</th>
								<th>{label[fWeek]}</th>
								<th>VS. LY</th>
								<th>VS. {tstype}</th>
							</tr>
							{!loadTable && tableContent.length > 0 && mapTableTotals(tableContent)}
							{!loadTable && tableContent.length > 0 && tableContent.map(mapTable)}
						</tbody>
					</table>
				</div>
			</section>

			<section className="content__block">
				<div className="row stack-chart">
					<div className="col--2">
						<h3 className="stack-chart__title">Month Graph</h3>
						<Stack id="monthGraph" data={monthData} channels={channels} totals={totals} isYTD={false}/>
					</div>
					<div className="col--2">
						<h3 className="stack-chart__title">YTD Graph</h3>
						<Stack id="ytdGraph" data={monthDataYTD} channels={channels} totals={totals} isYTD={true}/>
					</div>
					<div className="stack-chart__legend">
						{active && active.planChannels ? active.planChannels?.map((obj: IChannelTS, i: number) => {
							const channel = channels.find(channel => channel.channelName === obj.name);

							return (
								<div
									key={"barLegend" + obj.name}
									className="stack-chart__item"
								>
									<span
										className="dot"
										style={{ backgroundColor: channel?.color }}
									></span>
									<span className="stack-chart__label">{obj.name}</span>
								</div>
							);
						}) : <p>No data to show</p>}
					</div>
				</div>
			</section>
			</React.Fragment>
		)
	}

	return (
		<React.Fragment>
			{loading ? (
				<Loading />
			) : renderCountryCloseup()}
		</React.Fragment>
	);
};

export default CountryCloseup;
